import { EvalMetricsCard } from "@/components/eval/EvalMetricsCard";
import { EvalStatusBadge } from "@/components/eval/EvalStatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EvalService } from "@/gen/omnios/v1/eval_connect.js";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	Download,
	XCircle,
} from "lucide-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/eval/runs/$id")({
	component: RunDetail,
});

const evalClient = createClient(EvalService, transport);

function exportCsv(
	rows: Array<{
		rawText: string;
		expectedProductTitle: string;
		actualProductTitle: string;
		matchConfidence: number;
		rankOfCorrect: number;
		triagePath: string;
		isCorrect: boolean;
		unitCorrect: boolean;
		expectedUnit: string;
		actualUnit: string;
	}>,
) {
	const header =
		"raw_text,expected,actual,confidence,rank,triage,correct,unit_correct,expected_unit,actual_unit";
	const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
	const lines = rows.map((r) =>
		[
			esc(r.rawText),
			esc(r.expectedProductTitle),
			esc(r.actualProductTitle),
			r.matchConfidence.toFixed(4),
			r.rankOfCorrect || "",
			r.triagePath,
			r.isCorrect,
			r.unitCorrect,
			r.expectedUnit,
			r.actualUnit,
		].join(","),
	);
	const blob = new Blob([[header, ...lines].join("\n")], {
		type: "text/csv;charset=utf-8;",
	});
	const url = URL.createObjectURL(blob);
	Object.assign(document.createElement("a"), {
		href: url,
		download: "eval-results.csv",
	}).click();
	URL.revokeObjectURL(url);
}

function RunDetail() {
	const { t } = useTranslation("eval");
	const { id } = Route.useParams();
	const [verdictFilter, setVerdictFilter] = useState("");
	const [expandedRow, setExpandedRow] = useState<string | null>(null);

	const { data: run, isLoading: runLoading } = useQuery({
		queryKey: ["eval", "run", id],
		queryFn: () => evalClient.getEvalRun({ evalRunId: id }),
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			return status === "running" || status === "pending" ? 3000 : false;
		},
	});

	const {
		data: rowsPages,
		isLoading: rowsLoading,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: ["eval", "run-rows", id, verdictFilter],
		queryFn: ({ pageParam }) =>
			evalClient.listEvalRunRows({
				evalRunId: id,
				pageSize: 100,
				pageToken: pageParam,
				verdictFilter,
			}),
		initialPageParam: "",
		getNextPageParam: (last) => last.nextPageToken || undefined,
		enabled: run?.status === "completed",
	});

	const rows = rowsPages?.pages.flatMap((p) => p.rows) ?? [];
	const totalCount = rowsPages?.pages[0]?.totalCount ?? BigInt(0);

	if (runLoading) return <Skeleton className="h-64" />;
	if (!run) return <p>{t("not_found")}</p>;

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-lg font-semibold">
						{run.datasetName || run.datasetId.slice(0, 8)}
					</h2>
					<p className="text-sm text-muted-foreground">
						{run.algorithmVersion || "—"} · {run.matchMode} ·{" "}
						{run.modelName || "—"}
					</p>
				</div>
				<EvalStatusBadge status={run.status} />
			</div>

			{run.status === "running" && (
				<Card>
					<CardContent className="py-4">
						<div className="flex items-center gap-3">
							<div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
								<div
									className="h-full rounded-full bg-primary animate-pulse"
									style={{ width: "60%" }}
								/>
							</div>
							<span className="text-sm text-muted-foreground">
								{t("progress_running")}
							</span>
						</div>
					</CardContent>
				</Card>
			)}

			{run.status === "failed" && run.errorMessage && (
				<Card className="border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30">
					<CardContent className="py-3 text-sm text-red-800 dark:text-red-400">
						{run.errorMessage}
					</CardContent>
				</Card>
			)}

			{run.metrics && <EvalMetricsCard metrics={run.metrics} />}

			{run.status === "completed" && (
				<Card>
					<CardHeader className="flex flex-row items-center justify-between">
						<CardTitle className="text-base">
							{t("results_title")}
							<span className="ml-2 text-sm font-normal text-muted-foreground">
								{t("results_total", { count: totalCount.toString() })}
							</span>
						</CardTitle>
						<div className="flex items-center gap-3">
							<Tabs
								value={verdictFilter || "all"}
								onValueChange={(v) => setVerdictFilter(v === "all" ? "" : v)}
							>
								<TabsList>
									<TabsTrigger value="all">{t("filter_all")}</TabsTrigger>
									<TabsTrigger value="correct">
										{t("filter_correct")}
									</TabsTrigger>
									<TabsTrigger value="incorrect">
										{t("filter_incorrect")}
									</TabsTrigger>
								</TabsList>
							</Tabs>
							{rows.length > 0 && (
								<Button
									size="sm"
									variant="outline"
									onClick={() => exportCsv(rows)}
								>
									<Download className="mr-1 h-4 w-4" />
									{t("export_csv")}
								</Button>
							)}
						</div>
					</CardHeader>
					<CardContent>
						{rowsLoading ? (
							<Skeleton className="h-48" />
						) : !rows.length ? (
							<p className="text-sm text-muted-foreground">{t("no_results")}</p>
						) : (
							<>
								<div className="overflow-x-auto">
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead className="w-8" />
												<TableHead className="w-8" />
												<TableHead>{t("col_query")}</TableHead>
												<TableHead>{t("col_expected")}</TableHead>
												<TableHead>{t("col_actual")}</TableHead>
												<TableHead>{t("col_confidence")}</TableHead>
												<TableHead>{t("col_rank")}</TableHead>
												<TableHead>{t("col_triage")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{rows.map((row) => (
												<Fragment key={row.id}>
													<TableRow
														className={`cursor-pointer ${row.isCorrect ? "" : "bg-red-50/50 dark:bg-red-950/20"}`}
														onClick={() =>
															setExpandedRow(
																expandedRow === row.id ? null : row.id,
															)
														}
													>
														<TableCell>
															{expandedRow === row.id ? (
																<ChevronDown className="h-4 w-4 text-muted-foreground" />
															) : (
																<ChevronRight className="h-4 w-4 text-muted-foreground" />
															)}
														</TableCell>
														<TableCell>
															{row.isCorrect ? (
																<CheckCircle2 className="h-4 w-4 text-green-600" />
															) : (
																<XCircle className="h-4 w-4 text-red-500" />
															)}
														</TableCell>
														<TableCell className="max-w-xs truncate font-mono text-xs">
															{row.rawText}
														</TableCell>
														<TableCell className="max-w-[200px] truncate text-sm">
															{row.expectedProductTitle || "—"}
														</TableCell>
														<TableCell className="max-w-[200px] truncate text-sm">
															{row.actualProductTitle || "—"}
														</TableCell>
														<TableCell className="font-mono text-xs">
															{(row.matchConfidence * 100).toFixed(1)}%
														</TableCell>
														<TableCell className="font-mono text-xs">
															{row.rankOfCorrect || "—"}
														</TableCell>
														<TableCell>
															<Badge variant="outline" className="text-xs">
																{row.triagePath}
															</Badge>
														</TableCell>
													</TableRow>
													{expandedRow === row.id && (
														<TableRow>
															<TableCell
																colSpan={8}
																className="bg-muted/30 p-4"
															>
																<div className="space-y-3 text-sm">
																	{row.rewrittenQuery && (
																		<div>
																			<span className="text-xs font-medium text-muted-foreground">
																				{t("rewritten_query")}:
																			</span>{" "}
																			<span className="font-mono text-xs">
																				{row.rewrittenQuery}
																			</span>
																		</div>
																	)}
																	<div>
																		<span className="text-xs font-medium text-muted-foreground">
																			{t("col_unit_correct")}:
																		</span>{" "}
																		<span className="text-xs">
																			{row.unitCorrect ? "✓" : "✗"} (
																			{row.expectedUnit || "—"} →{" "}
																			{row.actualUnit || "—"})
																		</span>
																	</div>
																	{row.candidates.length > 0 && (
																		<div>
																			<span className="text-xs font-medium text-muted-foreground">
																				{t("candidates_title")}:
																			</span>
																			<div className="mt-1 rounded border">
																				<Table>
																					<TableHeader>
																						<TableRow>
																							<TableHead className="text-xs">
																								{t("candidate_rank")}
																							</TableHead>
																							<TableHead className="text-xs">
																								{t("candidate_title")}
																							</TableHead>
																							<TableHead className="text-xs">
																								{t("candidate_similarity")}
																							</TableHead>
																						</TableRow>
																					</TableHeader>
																					<TableBody>
																						{row.candidates.map((c) => (
																							<TableRow key={c.productId}>
																								<TableCell className="text-xs">
																									{c.rank}
																								</TableCell>
																								<TableCell className="text-xs">
																									{c.title}
																								</TableCell>
																								<TableCell className="font-mono text-xs">
																									{c.similarity.toFixed(4)}
																								</TableCell>
																							</TableRow>
																						))}
																					</TableBody>
																				</Table>
																			</div>
																		</div>
																	)}
																</div>
															</TableCell>
														</TableRow>
													)}
												</Fragment>
											))}
										</TableBody>
									</Table>
								</div>
								{hasNextPage && (
									<div className="pt-4 text-center">
										<Button
											variant="outline"
											size="sm"
											onClick={() => fetchNextPage()}
											disabled={isFetchingNextPage}
										>
											{isFetchingNextPage ? t("loading_more") : t("load_more")}
										</Button>
									</div>
								)}
							</>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
